import {
  converObjectErrorInString,
  convertObjectToCamelCase,
  convertObjectToSnakeCase,
} from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type {
  GeneratePurchaseOrderPayload,
  GeneratePurchaseOrderPreviewPayload,
  GeneratePurchaseOrderPreviewResponse,
  SupplierInvoiceProps,
  SupplierOrderWithPagesProps,
  SupplyItemsPreviewPayload,
  SupplyItemsPreviewResponse,
  SupplyOrderPreview,
  SupplyOrderPreviewResponse,
  SupplyOrderProps,
  SupplyOrderUpdateManagementPayload,
  SupplyPaymentProps,
  SupplyPaymentRequiredPayload,
} from '@/types'

import { useMutation, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import {
  BFF_GENERATE_PURCHASE_ORDER_URL,
  BFF_GENERATE_SUPPLY_ITEMS_PREVIEW_URL,
  BFF_GENERATE_SUPPLY_PDF_PREVIEW_URL,
  BFF_GENERATE_SUPPLY_PREVIEW_URL,
  BFF_SUPPLY_ORDER_MANAGMENT_URL,
  BFF_SUPPLY_ORDER_PAYMENT_REQUIRED_URL,
  BFF_SUPPLY_ORDER_URL,
  BFF_SUPPLY_ORDERS_URL,
  BFF_SUPPLY_PAYMENT_URL,
  BFF_SUPPLY_PAYMENTS_URL,
  BFF_SUPPLY_PREVIEW_URL,
  GENERATE_PURCHASE_ORDER_PDF_PREVIEW_URL,
  GENERATE_PURCHASE_ORDER_URL,
  SUPPLY_ORDER_INVOICE_URL,
  SUPPLY_ORDER_PAYMENT_URL,
  SUPPLY_ORDERS_URL,
} from './api.urls'

export const useUploadSupplierOrderInvoice = () => {
  const uploadSupplierOrderInvoice = async (payload: {
    uuid: string
    file: File
    type: 'xml' | 'pdf'
  }): Promise<{
    success: boolean
    message: string
    data?: SupplierInvoiceProps
  }> => {
    const formData = new FormData()

    if (payload.type === 'xml') {
      formData.append('xml_file', payload.file)
    }
    if (payload.type === 'pdf') {
      formData.append('pdf_file', payload.file)
    }

    try {
      const { data } = await api.patch(
        SUPPLY_ORDER_INVOICE_URL(payload.uuid),
        formData,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplierInvoiceProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.FILE.module],
    mutationFn: (payload: { uuid: string; file: File; type: 'xml' | 'pdf' }) =>
      uploadSupplierOrderInvoice(payload),
  })
}

export const useGeneratePurchaseOrder = () => {
  const generatePurchaseOrder = async (
    payload: GeneratePurchaseOrderPayload,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.post(BFF_GENERATE_PURCHASE_ORDER_URL, payload)

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.GENERATE_PURCHASE_ORDER.module],
    mutationFn: (payload: GeneratePurchaseOrderPayload) =>
      generatePurchaseOrder(payload),
  })
}

export const useUpdatePurchaseOrder = (uuid?: string) => {
  const updatePurchaseOrder = async (
    payload: GeneratePurchaseOrderPayload,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.patch(
        `${GENERATE_PURCHASE_ORDER_URL}${uuid}/`,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.GENERATE_PURCHASE_ORDER.module],
    mutationFn: (payload: GeneratePurchaseOrderPayload) =>
      updatePurchaseOrder(payload),
  })
}

export const useSupplierOrderCreate = () => {
  const supplierOrderCreate = async (
    payload: Partial<SupplyOrderProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.post(
        SUPPLY_ORDERS_URL,
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
    mutationFn: (payload: Partial<SupplyOrderProps>) =>
      supplierOrderCreate(payload),
  })
}

export const useSupplierOrderEdit = (uuid?: string) => {
  const supplierOrderEdit = async (
    payload: Partial<SupplyOrderProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const uuidAux = payload?.uuid || uuid

      if (payload?.uuid) {
        delete payload.uuid
      }

      const { data } = await api.patch(
        BFF_SUPPLY_ORDER_URL(uuidAux || ''),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, uuid],
    mutationFn: (payload: Partial<SupplyOrderProps>) =>
      supplierOrderEdit(payload),
  })
}

export const useSupplyItemsPreview = (
  payload: SupplyItemsPreviewPayload,
  isEnabled: boolean,
) => {
  const supplyItemsPreview = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyItemsPreviewResponse
  }> => {
    try {
      const { data } = await api.post(
        BFF_GENERATE_SUPPLY_ITEMS_PREVIEW_URL,
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyItemsPreviewResponse,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.SUPPLIER_ITEMS_PREVIEW.module, payload],
    queryFn: () => supplyItemsPreview(),
    enabled: isEnabled,
  })
}

export const useGeneratePurchaseOrderPreview = (
  payload: GeneratePurchaseOrderPreviewPayload,
  isEnabled: boolean,
) => {
  const generatePurchaseOrderPreview = async (): Promise<{
    success: boolean
    message: string
    data?: GeneratePurchaseOrderPreviewResponse
  }> => {
    try {
      const { data } = await api.post(BFF_GENERATE_SUPPLY_PREVIEW_URL, payload)

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as GeneratePurchaseOrderPreviewResponse,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.GENERATE_PURCHASE_ORDER_PREVIEW.module, payload],
    queryFn: () => generatePurchaseOrderPreview(),
    enabled: isEnabled,
  })
}

export const useGeneratePurchaseOrderPDFPreview = (
  payload: GeneratePurchaseOrderPayload,
  isEnabled: boolean,
) => {
  const generatePurchaseOrderPDFPreview = async (): Promise<{
    success: boolean
    message: string
    data?: Blob
  }> => {
    try {
      const { data } = await api.post(
        BFF_GENERATE_SUPPLY_PDF_PREVIEW_URL,
        { ...payload, sendPo: false },
        {
          responseType: 'blob',
        },
      )

      return {
        success: true,
        message: 'successfully!',
        data,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [
      QUERIES_ADMIN.GENERATE_PURCHASE_ORDER_PDF_PREVIEW.module,
      payload,
    ],
    queryFn: () => generatePurchaseOrderPDFPreview(),
    enabled: isEnabled,
  })
}

export const useSupplyOrderPreview = (
  payload: SupplyOrderPreview,
  isEnabled: boolean,
) => {
  const supplierOrderPreview = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderPreviewResponse
  }> => {
    try {
      const { data } = await api.post(BFF_SUPPLY_PREVIEW_URL, payload)

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderPreviewResponse,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER_PREVIEW.module, payload],
    queryFn: () => supplierOrderPreview(),
    enabled: isEnabled,
  })
}

export const useSupplierOrderPDFPreview = (
  payload: SupplyOrderPreview,
  isEnabled: boolean,
) => {
  const supplierOrderPDFPreview = async (): Promise<{
    success: boolean
    message: string
    data?: Blob
  }> => {
    try {
      const { data } = await api.post(
        GENERATE_PURCHASE_ORDER_PDF_PREVIEW_URL,
        convertObjectToSnakeCase(payload),
        {
          responseType: 'blob',
        },
      )

      return {
        success: true,
        message: 'successfully!',
        data,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER_PDF_PREVIEW.module, payload],
    queryFn: () => supplierOrderPDFPreview(),
    enabled: isEnabled,
  })
}

export const useSupplierOrderList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getOrderSaleOrderList = async (): Promise<{
    success: boolean
    message: string
    data?: SupplierOrderWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_SUPPLY_ORDERS_URL, {
        params: {
          ...(convertObjectToCamelCase(formattedParams) as Record<
            string,
            string
          >),
          offset,
        },
      })

      return {
        success: true,
        message: 'Supplier Order fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplierOrderWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get supplier order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, params],
    queryFn: () => getOrderSaleOrderList(),
    enabled: isEnabled,
  })
}

export const useSupplierOrderDetail = (uuid?: string) => {
  const getSupplierOrderDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.get(BFF_SUPPLY_ORDER_URL(`${uuid}`))

      return {
        success: true,
        message: 'Supplier Order fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Supplier Order!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, uuid],
    queryFn: () => getSupplierOrderDetail(),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  })
}

export const useSupplierOrderDetailBySupplyQuotation = () => {
  const getSupplierOrderDetailBySupplyQuotation = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplierOrderWithPagesProps
  }> => {
    try {
      const { data } = await api.get(
        `${SUPPLY_ORDERS_URL}?supply_quotation__uuid=${uuid}`,
      )

      return {
        success: true,
        message: 'Supplier Order fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplierOrderWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Supplier Order!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
    mutationFn: (uuid: string) => getSupplierOrderDetailBySupplyQuotation(uuid),
  })
}

export const useSupplierPaymentEdit = (uuid: string) => {
  const supplierOrderEdit = async (
    payload: Partial<SupplyPaymentProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyPaymentProps
  }> => {
    try {
      const { data } = await api.patch(
        SUPPLY_ORDER_PAYMENT_URL(`${uuid}`),
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyPaymentProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
    mutationFn: (payload: Partial<SupplyPaymentProps>) =>
      supplierOrderEdit(payload),
  })
}

export const useSupplyOrderManagmentEdit = (uuid?: string) => {
  const SupplyOrderManagmentEdit = async (
    payload: SupplyOrderUpdateManagementPayload,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const uuidAux = payload?.supplyOrder?.uuid || uuid

      if (payload?.supplyOrder?.uuid) {
        delete payload?.supplyOrder?.uuid
      }

      const { data } = await api.patch(
        BFF_SUPPLY_ORDER_MANAGMENT_URL(uuidAux || ''),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_ORDER_MANAGMENT.module, uuid],
    mutationFn: (payload: SupplyOrderUpdateManagementPayload) =>
      SupplyOrderManagmentEdit(payload),
  })
}

export const useSupplyPaymentRequired = () => {
  const supplyPaymentRequired = async (
    payload: SupplyPaymentRequiredPayload,
  ): Promise<{
    success: boolean
    message: string
    data?: null
  }> => {
    try {
      const { data } = await api.post(
        BFF_SUPPLY_ORDER_PAYMENT_REQUIRED_URL,
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as null,
      }
    } catch (err) {
      let message = ''

      if (isAxiosError(err) && err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLY_ORDER_PAYMENT_REQUIRED.module],
    mutationFn: (payload: SupplyPaymentRequiredPayload) =>
      supplyPaymentRequired(payload),
  })
}

export const useSupplyPaymentDetail = (uuid?: string) => {
  const getSupplyPaymentDetail = async (): Promise<{
    success: boolean
    message: string
    data?: SupplyPaymentProps
  }> => {
    try {
      const { data } = await api.get(BFF_SUPPLY_PAYMENT_URL(`${uuid}`))

      return {
        success: true,
        message: 'Supply payment fetched successfully!',
        data: convertObjectToCamelCase(data) as SupplyPaymentProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Supply payment!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SUPPLIER_PAYMENT.module, uuid],
    queryFn: () => getSupplyPaymentDetail(),
    enabled: !!uuid,
  })
}

export const useSupplyPaymentCreate = () => {
  const SupplyPaymentCreate = async (
    payload: SupplyPaymentProps,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.post(
        BFF_SUPPLY_PAYMENTS_URL,
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_PAYMENT.module],
    mutationFn: (payload: SupplyPaymentProps) =>
      SupplyPaymentCreate(payload),
  })
}

export const useSupplyPaymentEdit = (uuid?: string) => {
  const SupplyPaymentEdit = async (
    payload: SupplyPaymentProps,
  ): Promise<{
    success: boolean
    message: string
    data?: SupplyOrderProps
  }> => {
    try {
      const { data } = await api.patch(
        BFF_SUPPLY_PAYMENT_URL(uuid || ''),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as SupplyOrderProps,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SUPPLIER_PAYMENT.module, uuid],
    mutationFn: (payload: SupplyPaymentProps) =>
      SupplyPaymentEdit(payload),
  })
}